import 'polyfills';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
import dayjsBetween from 'dayjs/plugin/isBetween';
import dayjsRelativeTime from 'dayjs/plugin/relativeTime';
import dayjsTimezone from 'dayjs/plugin/timezone';
import dayjsUTC from 'dayjs/plugin/utc';
import 'dayjs/locale/es';
import 'dayjs/locale/ru';
import 'dayjs/locale/zh';
import 'dayjs/locale/vi';
import { AbortController } from 'node-abort-controller';
import type { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { App } from 'App';
import { store } from 'store';
import { theme } from 'theme';
import { i18nReady } from 'translations';
import { initThirdPartyAnalytics } from 'utils/analytics';
import { reportWebVitals } from 'utils/reportWebVitals';

import 'sentry';

dayjs.extend(dayjsUTC);
dayjs.extend(dayjsDuration);
dayjs.extend(dayjsBetween);
dayjs.extend(dayjsRelativeTime);
dayjs.extend(dayjsTimezone);

// add polyphill for old browsers, like India UC Browser to prevent crashes
if (!global.AbortController) {
  global.AbortController = AbortController as typeof globalThis.AbortController;
}

if (process.env.REACT_APP_ENV === 'production') {
  initThirdPartyAnalytics({
    gtmId: process.env.REACT_APP_GTM_CONTAINER_ID,
    gaMeasurementId: process.env.REACT_APP_GA_MEASUREMENT_ID,
  });
}

const options: Partial<PostHogConfig> = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
  loaded: (posthog) => {
    // Disable PostHog in development and staging
    if (['development', 'staging'].includes(process.env.REACT_APP_ENV)) {
      posthog.opt_out_capturing();
    }

    // Start collecting only now: before init the client can't capture, and web-vitals observers are
    // buffered so nothing already measured (TTFB/FCP) is lost by starting here.
    if (process.env.REACT_APP_ENV === 'production') {
      reportWebVitals(posthog);
    }
  },
  // Every user action is already tracked via explicit posthog.capture() calls (see constants/posthogEvents.ts),
  // so blanket autocapture would just duplicate that volume at extra cost.
  autocapture: false,
  // Pageleaves aren't used for analysis and are already implied by the next pageview/session end.
  // Pageviews are kept - GA4 covers overall traffic, but PostHog's own funnels/insights rely on them too.
  capture_pageleave: false,
  enable_heatmaps: true,
  capture_heatmaps: true,
  session_recording: {
    // Don't record sessions by default - recording is turned on explicitly for critical flows
    // (order/payment, extending a proxy - see utils/sessionRecording and its call sites) and when
    // a genuine app crash or failed request (5xx) reaches Sentry (see sentry.ts), so replay cost
    // is paid only where it matters, not for every ordinary session.
    sampleRate: 0,
    maskAllInputs: false,
    maskInputOptions: { password: true },
    maskInputFn: (text, element) => {
      if (
        // @ts-expect-error - This is a proper way to get the field name
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        element?.attributes.name?.value?.toLowerCase()?.includes('password') ||
        // @ts-expect-error - This is a proper way to get the field name
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        element?.attributes.name?.value?.toLowerCase()?.includes('passwd')
      ) {
        return '*'.repeat(text.length);
      }

      return text;
    },
    maskTextSelector: '#password, #passwd, #passwd2, #newPassword, #newPasswordConfirmation',
  },
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(document.getElementById('root')!);

// English is bundled so this resolves immediately for most users; for other locales it waits for
// the language's JSON chunks so the first paint never shows raw translation keys. `finally` (not
// `then`) so a failed locale fetch still renders the app, falling back to English.
i18nReady.finally(() => {
  root.render(
    // eslint-disable-next-line i18next/no-literal-string
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <PostHogProvider apiKey={process.env.REACT_APP_POSTHOG_KEY} options={options}>
        <Provider store={store}>
          <ThemeProvider theme={theme} defaultMode="light" disableTransitionOnChange>
            <CssBaseline />
            <App />
          </ThemeProvider>
        </Provider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>,
  );
});
